import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText } from 'lucide-react';
import { PerformanceData } from '@/hooks/usePerformanceData';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface ExportButtonProps {
  data: PerformanceData | null;
  dateRange: { from: Date | null; to: Date | null };
}

export function ExportButton({ data, dateRange }: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);

  const exportToCSV = () => {
    if (!data) {
      toast.error('No data to export');
      return;
    }

    setIsExporting(true);

    try {
      const headers = [
        'Recruiter Name',
        'Email',
        'Open Positions',
        'Total Candidates',
        'Candidates Sourced',
        'Candidates Interviewed',
        'Candidates Selected',
        'Interview Rate (%)',
        'Selection Rate (%)',
        'Conversion Rate (%)',
      ];

      const rows = data.recruiters.map((r) => [
        r.recruiterName,
        r.recruiterEmail,
        r.openPositions,
        r.totalCandidates,
        r.candidatesSourced,
        r.candidatesInterviewed,
        r.candidatesSelected,
        r.interviewRate.toFixed(2),
        r.selectionRate.toFixed(2),
        r.conversionRate.toFixed(2),
      ]);

      // Add team averages row
      rows.push([
        'Team Average',
        '-',
        data.teamAverages.openPositions.toFixed(1),
        data.teamAverages.totalCandidates.toFixed(1),
        '-',
        '-',
        '-',
        data.teamAverages.interviewRate.toFixed(2),
        data.teamAverages.selectionRate.toFixed(2),
        data.teamAverages.conversionRate.toFixed(2),
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const dateStr = dateRange.from && dateRange.to
        ? `${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}`
        : format(new Date(), 'yyyy-MM-dd');
      
      link.setAttribute('href', url);
      link.setAttribute('download', `recruiter_performance_${dateStr}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Performance data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToJSON = () => {
    if (!data) {
      toast.error('No data to export');
      return;
    }

    setIsExporting(true);

    try {
      const exportData = {
        exportDate: new Date().toISOString(),
        dateRange: {
          from: dateRange.from?.toISOString() || null,
          to: dateRange.to?.toISOString() || null,
        },
        teamAverages: data.teamAverages,
        totalMetrics: data.totalMetrics,
        recruiters: data.recruiters,
      };

      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const dateStr = dateRange.from && dateRange.to
        ? `${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}`
        : format(new Date(), 'yyyy-MM-dd');
      
      link.setAttribute('href', url);
      link.setAttribute('download', `recruiter_performance_${dateStr}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Performance data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={isExporting || !data}>
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportToCSV}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportToJSON}>
          <FileText className="h-4 w-4 mr-2" />
          Export as JSON
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
